import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { createQueryString } from '../../../lib/utils';
import { getConfigurations } from '../../../modules/config';
import { getTestCasesNotInSuite } from '../../../modules/testsuites';
import {
  techLayerFilters
} from '../../../static-data/DropdownOptions';
import SearchBar from '../../elements/SearchBar';
import TableComponent from '../../elements/TableComponent';
import { testCasestableHeader } from '../test-case/testCaseTableHead';

const AddTestCaseInSuite = ({ selectedSuite, selected, setSelected }) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [testCases, setTestCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const perPage = 20;
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState({
    status: 'All',
    technologyLayer: 'All',
  });

  const fetchTestCases = async (
    currentPage = 1,
    searchValue = '',
    queryString = ''
  ) => {
    setLoading(true);
    try {
      const response = await getTestCasesNotInSuite(
        selectedSuite?.id,
        projectId,
        currentPage,
        perPage,
        searchValue,
        queryString
      );
      setTestCases(response?.testcases || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      const errorMessage = error?.message || 'Failed to fetch test cases.';
      addNotification('danger', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryString = createQueryString(filters);
    fetchTestCases(page, debouncedSearchQuery, queryString);
  }, [page, debouncedSearchQuery, filters]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilters = (event) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const fetchStatuses = async () => {
    try {
      const response = await getConfigurations('testcasestatus', projectId);
      if (response) {
        setStatusOptions(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  return (
    <>
      <Row>
        <Col md={6} className="d-flex align-items-end">
          <SearchBar
            placeholder="Search test case(s) to add"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            clearSearch={() => setSearchValue('')}
          />
        </Col>
        <Col md={6} className="d-flex gap-3 justify-content-end">
          <Form.Group controlId="technologyLayer" className="mb-0 w-50">
            <Form.Label>Technology Layer</Form.Label>
            <Form.Select
              name="technologyLayer"
              value={filters.technologyLayer}
              onChange={handleFilters}>
              {techLayerFilters?.map((tech) => (
                <option key={tech} value={tech}>
                  {tech}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="status" className="mb-0 w-50">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={filters.status}
              onChange={handleFilters}>
              <option value="All">All</option>
              {statusOptions.filter((status) => status.name !== 'Archived').map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <TableComponent
            data={testCases || []}
            headerTabs={testCasestableHeader}
            showCheckbox
            page={page}
            setPage={setPage}
            perPage={perPage}
            order={order}
            setOrder={setOrder}
            defaultSort="created_at"
            selected={selected}
            isLoading={loading}
            striped
            setSelected={setSelected}
            totalCount={totalCount}
            showPages
            handlePageChange={handlePageChange}
            tablekey="test cases"
          />
        </Col>
      </Row>
    </>
  );
};

export default AddTestCaseInSuite;
